import { useEffect, useState } from "react";
import axios from "axios";
import { Wrench, Instagram, Mail, Phone, ShieldCheck } from "lucide-react";
import { DevistoLogo } from "@/components/DevistoLogo";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BYPASS_KEY = "devisto_maintenance_bypass";

// Routes toujours accessibles (connexion admin, admin, suivi client, passeport QR)
const ALLOWED = ["/connexion", "/admin", "/suivi/", "/passeport/", "/pt/", "/reinitialiser/", "/devis/", "/studio", "/assistant-devis"];

export const MaintenanceGate = ({ children }) => {
  const { t } = useLanguage();
  const [state, setState] = useState({ loading: true, enabled: false, message: "", until: "" });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("apercu") === "1") sessionStorage.setItem(BYPASS_KEY, "1");
    axios.get(`${API}/maintenance`)
      .then(({ data }) => setState({ loading: false, ...data }))
      .catch(() => setState({ loading: false, enabled: false, message: "", until: "" }));
  }, []);

  if (state.loading) return null;

  const path = window.location.pathname;
  const allowed = ALLOWED.some((p) => path.startsWith(p));
  const isAdmin = localStorage.getItem("devisto_role") === "admin" || !!localStorage.getItem("devisto_admin_token");
  const bypass = sessionStorage.getItem(BYPASS_KEY) === "1";

  if (!state.enabled || allowed || isAdmin || bypass) return children;

  return (
    <div data-testid="maintenance-page" className="font-body min-h-screen bg-[#0F2645] text-white flex flex-col items-center justify-center px-6 py-16 text-center relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #3A5CC8 0, transparent 45%), radial-gradient(circle at 80% 70%, #3A5CC8 0, transparent 40%)" }} />
      <div className="relative max-w-xl">
        <DevistoLogo className="h-9 w-auto mx-auto" variant="white" />
        <span className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-blue-200">
          <Wrench className="w-3.5 h-3.5" /> {t("Maintenance en cours")}
        </span>
        <h1 className="mt-6 font-heading text-4xl sm:text-5xl font-bold tracking-tight">
          {t("Nous améliorons Devisto")}
        </h1>
        <p className="mt-5 text-base text-blue-100/80 leading-relaxed">
          {state.message || t("Notre plateforme est momentanément indisponible le temps d'une mise à jour technique. Merci de votre patience — nous revenons très vite.")}
        </p>
        {state.until && (
          <p data-testid="maintenance-until" className="mt-3 text-sm font-semibold text-blue-200">{t("Retour prévu")} : {state.until}</p>
        )}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <a href="mailto:info@devis-to.be" data-testid="maintenance-email" className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-[#0F2645] hover:bg-[#EEF1FC] transition-colors duration-200">
            <Mail className="w-4 h-4" /> info@devis-to.be
          </a>
          <a href="tel:+32470000000" data-testid="maintenance-phone" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-colors duration-200">
            <Phone className="w-4 h-4" /> {t("Nous appeler")}
          </a>
          <a href="https://www.instagram.com/devis.to" target="_blank" rel="noopener noreferrer" data-testid="maintenance-instagram" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-colors duration-200">
            <Instagram className="w-4 h-4" /> Instagram
          </a>
        </div>
        <p className="mt-10 inline-flex items-center gap-2 text-xs text-blue-200/60">
          <ShieldCheck className="w-3.5 h-3.5" /> {t("Vos données et vos demandes sont conservées en sécurité.")}
        </p>
      </div>
    </div>
  );
};
